import * as React from "react";
import { Text } from "@fluentui/react/lib/Text";
import { Icon } from "@fluentui/react/lib/Icon";
import { Input } from "@mui/material";
import { resolveFtlOfferBaseUsd } from "@shared/utils/resolveFtlOfferBaseUsd";
import { QuotedRate } from "@src/types/api";

const formatCurrency = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const cleanCarrierName = (name: string) => {
  return name
    .replace(/\s+(LLC|INC|CO|LTD)$/i, "")
    .replace(/\s+/g, " ")
    .trim();
};

interface OfferCardProps {
  offer: QuotedRate;
  selectOffer: (offerId: string | null) => void;
  selectedOffer: string | null;
  cardTitle?: string;
  logoSrc?: string;
  logoAlt?: string;
  logoHeight?: number;
  showConfidence?: boolean;
  confidenceValue?: number | null;
  isBestRate?: boolean;
  marginPct?: number;
  onMarginChange?: (marginPct: number) => void;
  hideMarginControls?: boolean;
  showTmsCostBreakdown?: boolean;
  selectionLocked?: boolean;
}
const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;
const marginArrowButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  padding: 0,
  border: "1px solid #c8c6c4",
  borderRadius: 2,
  background: "#fff",
  cursor: "pointer",
  color: "#323130",
};

export default function OfferCard({
  offer,
  selectOffer,
  selectedOffer,
  cardTitle,
  logoSrc,
  logoAlt,
  logoHeight = 20,
  showConfidence = true,
  confidenceValue = null,
  isBestRate = false,
  marginPct = 0,
  onMarginChange,
  hideMarginControls = false,
  showTmsCostBreakdown = false,
  selectionLocked = false,
}: OfferCardProps) {
  const handleSelectOffer = () => {
    if (selectionLocked) return;
    const newSelection = selectedOffer === offer.id ? null : offer.id;

    selectOffer(newSelection);
  };
  const isSelected = selectionLocked || selectedOffer === offer.id;
  const basePrice = resolveFtlOfferBaseUsd(offer);
  const effectivePrice =
    typeof marginPct === "number" && !Number.isNaN(marginPct)
      ? basePrice * (1 + marginPct / 100)
      : basePrice;
  const marginInputValue =
    typeof marginPct === "number" && !Number.isNaN(marginPct) && marginPct !== 0 ? marginPct : "";
  const carrierName = cleanCarrierName(offer.name || "");
  const confidence = confidenceValue ?? offer.confidenceLevel ?? 0;

  return (
    <div
      className="offer-card"
      onClick={handleSelectOffer}
      style={{
        padding: "16px 20px",
        border: isSelected ? "2px solid #0078d4" : "1px solid #e1e1e1",
        borderRadius: "8px",
        marginBottom: "12px",
        cursor: selectionLocked ? "default" : "pointer",
        transition: "all 0.15s ease",
        backgroundColor: isSelected ? "#f8f9ff" : "#ffffff",
        transform: isSelected ? "translateY(-1px)" : "translateY(0)",
        boxShadow: isSelected ? "0 2px 8px rgba(0, 120, 212, 0.1)" : "0 1px 3px rgba(0, 0, 0, 0.1)",
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
        }
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={logoAlt ?? "Provider logo"}
                style={{
                  height: `${logoHeight}px`,
                  width: "auto",
                  objectFit: "contain",
                  marginBottom: "8px",
                  display: "block",
                }}
              />
            ) : null}
            {!logoSrc && cardTitle ? (
              <Text
                variant="small"
                block
                style={{
                  fontSize: "11px",
                  color: "#605e5c",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  marginBottom: "6px",
                }}
              >
                {cardTitle}
              </Text>
            ) : null}

            {isBestRate && (
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#107c41",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: "600",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Best Price
              </div>
            )}

            {!logoSrc && (
              <Text
                variant="medium"
                block
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#323130",
                  lineHeight: "20px",
                  marginBottom: "4px",
                }}
              >
                {carrierName}
              </Text>
            )}

            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <Text
                variant="small"
                style={{
                  fontSize: "12px",
                  color: "#605e5c",
                  fontWeight: "400",
                }}
              >
                {offer.distance || 0} miles
              </Text>
              {showConfidence !== false && (
                <Text
                  variant="small"
                  style={{
                    fontSize: "12px",
                    color: "#605e5c",
                    fontWeight: "400",
                  }}
                >
                  • CI: {confidence}%
                </Text>
              )}
            </div>
          </div>

          {!showTmsCostBreakdown ? (
            <Text
              variant="large"
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#323130",
                lineHeight: "24px",
                marginLeft: "16px",
                flexShrink: 0,
              }}
            >
              {formatCurrency(effectivePrice)}
            </Text>
          ) : null}
        </div>

        {showTmsCostBreakdown &&
        (typeof offer.tmsCost === "number" || typeof offer.tmsRate === "number") ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginTop: "4px",
              marginBottom: "8px",
              gap: "16px",
            }}
          >
            <div>
              <Text
                variant="small"
                styles={{ root: { fontSize: "11px", color: "#605e5c", fontWeight: 600 } }}
              >
                Cost
              </Text>
              <Text
                variant="large"
                style={{ fontSize: "20px", fontWeight: 700, color: "#323130", lineHeight: "26px" }}
              >
                {formatCurrency(Number(offer.tmsCost ?? 0))}
              </Text>
            </div>
            <div style={{ textAlign: "right" }}>
              <Text
                variant="small"
                styles={{ root: { fontSize: "11px", color: "#605e5c", fontWeight: 600 } }}
              >
                Suggested billed price
              </Text>
              <Text
                variant="large"
                style={{ fontSize: "20px", fontWeight: 700, color: "#323130", lineHeight: "26px" }}
              >
                {formatCurrency(Number(offer.tmsRate ?? offer.billed ?? 0))}
              </Text>
            </div>
          </div>
        ) : null}

        {!hideMarginControls ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              width: "100%",
              minWidth: 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Text
              variant="small"
              style={{
                fontSize: "12px",
                color: "#323130",
                fontWeight: "400",
                flexShrink: 0,
              }}
            >
              {formatCurrency(basePrice)} +
            </Text>
            <Text
              variant="small"
              style={{
                fontSize: "12px",
                color: "#323130",
                fontWeight: "400",
                flexShrink: 0,
              }}
            >
              Margin
            </Text>
            <Input
              type="number"
              value={marginInputValue}
              placeholder="0"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const next = parseFloat(e.target.value);

                onMarginChange?.(Number.isNaN(next) ? 0 : roundToTwoDecimals(next));
              }}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{
                width: 44,
                marginLeft: "auto",
                "& input": {
                  padding: "0 4px",
                  fontSize: "14px",
                  textAlign: "right",
                  color: "#323130",
                  "&::placeholder": {
                    color: "#605e5c",
                    opacity: 1,
                  },
                  MozAppearance: "textfield",
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                },
                "& fieldset": {
                  borderColor: "#c8c6c4",
                },
              }}
            />
            <Text
              variant="small"
              style={{
                fontSize: "12px",
                color: "#323130",
                fontWeight: "400",
                flexShrink: 0,
              }}
            >
              %
            </Text>
            <span style={{ width: "8px", flexShrink: 0 }} aria-hidden />
            <button
              type="button"
              aria-label="Increase margin"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMarginChange?.(roundToTwoDecimals(marginPct + 0.5));
              }}
              style={marginArrowButtonStyle}
            >
              <Icon iconName="ChevronUp" styles={{ root: { fontSize: 12 } }} />
            </button>
            <button
              type="button"
              aria-label="Decrease margin"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const next = Math.max(0, marginPct - 0.5);

                onMarginChange?.(roundToTwoDecimals(next));
              }}
              style={marginArrowButtonStyle}
            >
              <Icon iconName="ChevronDown" styles={{ root: { fontSize: 12 } }} />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
